

import { Component, Vue, Prop } from "vue-property-decorator"
import { namespace, Getter } from "vuex-class"
import DataForm from "../DataForm.vue"
import { TemplateDetails, Team, Template } from "@/types"
import RunningTotalsSelector from "../Schedules/RunningTotalsSelector.vue"
import { TenantFeatures } from "@/types/tenantFeatures"

const setup = namespace("setup")

@Component({
	components: {
		"data-form": DataForm
	}
})
export default class TemplateCadence extends Vue {
	@Prop()
	public template!: TemplateDetails

	@Prop({ type: Boolean, default: true })
	public canEdit!: boolean;

	@setup.Getter("getTeams")
	teams!: Team[];

	@Getter("getCaseName") getCaseName!: string;
	@Getter("getRecordName") getRecordName!: string;
	@Getter getIsFeatureEnabled!: (feature: string) => boolean;

	@Prop({ default: () => [] })
	private templates!: Template[];

	private runningTotalsFeedback = ""

	public async created () {
		if (this.template) {
			const cadenceDetails = this.template.cadenceDetails
			Vue.set(cadenceDetails!, "disablePermissionsTeams", cadenceDetails!.disablePermissions ? this.teams.filter(t => cadenceDetails!.disablePermissions.some(ut => ut.id === t.id)) : [])

			// Remove any falsy values from cadence fields as these cause issues with v-select
			if (this.template.cadenceDetails?.cadenceFields) {
				this.template.cadenceDetails.cadenceFields = this.template.cadenceDetails?.cadenceFields?.filter((field: any) => !!field)
			}

			// default cadenceDefaultSet to true if not set or feature is disabled
			if (!this.cadenceDefaultSetFeatureIsEnabled ||
					this.template.cadenceDetails!.cadenceDefaultSet === undefined ||
					this.template.cadenceDetails!.cadenceDefaultSet === null) {
				Vue.set(cadenceDetails!, "cadenceDefaultSet", true)
			}
		}
	}

	public get columns (): any[] {
		if (!this.template) {
			return []
		}

		return [
			{
				key: "cadenceTemplateId",
				label: "Template",
				dataType: "list",
				valueColumn: "cadenceTemplateId",
				options: this.templates,
				inModal: (_item: any) => true,
				inTable: false,
				placeholder: "Current Template"
			},
			{
				key: "cadenceFields",
				label: "Fields",
				dataType: "list-multiple",
				valueColumn: "cadenceFields",
				options: this.availableCadenceFields,
				inModal: (_item: any) => true,
				inTable: false
			},
			{
				key: "runningTotalSectionFields",
				label: "Running Total Fields",
				valueColumn: "runningTotalSectionFields",
				dynamicComponent: RunningTotalsSelector,
				inModal: (_item: any) => this.cadenceRunningTotalsFeatureIsEnabled,
				inTable: false,
				componentProperties: {
					template: this.template
				},
				errors: (item: any) => this.runningTotalsFeedback,
				eventHandlers: {
					"validation-changed": (feedback: string) => this.runningTotalsFeedback = feedback
				}
			},
			{
				key: "isCase",
				label: "Keep promoted status",
				dataType: "checkbox",
				valueColumn: "isCase",
				inModal: (_item: any) => true,
				inTable: false,
				tooltip: `When enabled transfer promoted status over to new cadence ${this.getRecordName}/${this.getCaseName}.`
			},
			{
				key: "cadencePeriod",
				label: "Period(Days)",
				dataType: "number",
				valueColumn: "cadencePeriod",
				errors: (item: any) => (item.cadencePeriod >= 0) ? null : "Please enter a positive number",
				componentProperties: {
					precision: 0
				}
			},
			{
				key: "cadenceDefaultSet",
				label: "Enable Cadence by Default",
				dataType: "checkbox",
				valueColumn: "cadenceDefaultSet",
				inModal: () => this.cadenceDefaultSetFeatureIsEnabled
			},
			{
				key: "disablePermissions",
				hasTemplate: true,
				label: "Disable Permissions",
				dataType: "list-multiple",
				valueColumn: "disablePermissionsTeams",
				options: this.teams,
				inTable: false,
				placeholder: "Any"
			}
		]
	}

	/**
	 * List of all the built-in fields that can be used for cadence
	 */
	public get builtInFields (): any[] {
		if (!this.template) {
			return []
		}

		return [
			{
				id: "title",
				title: "Title"
			},
			{
				id: "summary",
				title: "Summary"
			},
			{
				id: "severity",
				title: "Severity"
			},
			{
				id: "areas",
				title: "Affected Areas"
			},
			{
				id: "tags",
				title: "Tags"
			}
		]
	}

	/**
	 * List of all the fields added to the template
	 */
	get addedFields () {
		const fields: any[] = []

		if (this.template) {
			// Ensure no null fields are included.
			this.template.fields.filter(field => !!field)
				.forEach(f => {
					const field: any = { id: f.field.id, title: f.field.title, description: f.field.description }
					fields.push(field)
				})
		}
		return fields
	}

	/**
	 * List of all fields that can be used for cadence
	 * Ensure no null fields are included.
	 */
	get availableCadenceFields () {
		return this.builtInFields.concat(this.addedFields).filter(field => !!field)
	}

	get cadenceRunningTotalsFeatureIsEnabled (): boolean {
		return this.getIsFeatureEnabled(TenantFeatures.CadenceRunningTotals)
	}

	onTemplateCadenceValidationChanged (isValid: boolean) {
		this.$emit("validation-changed", isValid)
	}

	get cadenceDefaultSetFeatureIsEnabled (): boolean {
		return this.getIsFeatureEnabled(TenantFeatures.CadenceDefaultSet)
	}
}

